// Federation auth diagnostics — decodes the local auth state without
// hitting the network so callers can tell at a glance whether the
// governance Supabase JWT (required by the workspace API wrapper) is
// present, valid, and carries the workspace_id claim.
//
// Why this exists: the wrapper at /workspace/* requires a Supabase JWT
// with `workspace_id` (or `app_metadata.workspace_id` /
// `app_metadata.active_workspace_id`). The Symbols SDK's own token from
// `/core/auth/login` does NOT carry that claim, so calls authed with the
// SDK token alone get 401. The federation bridge (`loginAll` in
// `@symbo.ls/sdk-bridge`) signs the user into BOTH tenants and writes
// the governance JWT to localStorage under `sb-<gov-ref>-auth-token`.
// When auth fails the most common causes are:
//   1. governance JWT missing (federation never ran or was cleared)
//   2. governance JWT expired
//   3. governance JWT lacks workspace_id claim
//   4. SDK token present but no governance — wrapper sees the wrong tenant
//
// Use:
//   import { diagnoseAuth } from '<shared>/functions/diagnoseAuth.js'
//   console.table(diagnoseAuth())
// Or paste the body of `runAuthDiagnostic()` into a browser console for
// a no-build inspection.

import { RAW_SUPABASE_URL } from './supabaseConfig.js'

const _decodeJwt = (token) => {
  if (!token || typeof token !== 'string') return null
  const parts = token.split('.')
  if (parts.length !== 3) return null
  try {
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
    return JSON.parse(json)
  } catch {
    return null
  }
}

const _readGovernanceTokenRaw = () => {
  if (typeof window === 'undefined') return { ref: null, raw: null, parsed: null }
  const ref = (RAW_SUPABASE_URL || '').split('//')[1]?.split('.')[0] || null
  if (!ref) return { ref: null, raw: null, parsed: null }
  let raw = null
  try { raw = window.localStorage.getItem(`sb-${ref}-auth-token`) } catch { raw = null }
  if (!raw) return { ref, raw: null, parsed: null }
  try { return { ref, raw, parsed: JSON.parse(raw) } }
  catch { return { ref, raw, parsed: null } }
}

const _readSdkToken = () => {
  if (typeof window === 'undefined') return null
  try { return window.localStorage.getItem('symbols_access_token') } catch { return null }
}

// Public — return the governance JWT's payload (or `{}` when there's no
// token). Spread-friendly so callers can `{...readGovernanceClaims()}`
// without null guards. Merges top-level + `app_metadata` because the
// bridge writes some claims (orgs, role, workspace_id, ...) into either
// scope across deploys.
export const readGovernanceClaims = () => {
  const { parsed } = _readGovernanceTokenRaw()
  const token = parsed?.access_token || parsed?.currentSession?.access_token
  const payload = _decodeJwt(token)
  if (!payload) return {}
  return { ...(payload.app_metadata || {}), ...payload }
}

const _expiryStatus = (epochSec) => {
  if (!epochSec) return { expired: null, secondsLeft: null }
  const now = Date.now() / 1000
  const left = epochSec - now
  return { expired: left <= 0, secondsLeft: Math.floor(left) }
}

const _claimWorkspaceId = (claims) => {
  if (!claims) return null
  return (
    claims.workspace_id ||
    claims.workspaceId ||
    claims.app_metadata?.workspace_id ||
    claims.app_metadata?.active_workspace_id ||
    null
  )
}

/**
 * Returns a structured snapshot of the local auth state. No network calls.
 *
 * Shape:
 *   {
 *     governance: {
 *       ref, present, accessToken, expiresAt, expiresInSec, expired,
 *       userId, email, workspaceIdClaim, missingClaim, refreshTokenPresent
 *     },
 *     sdk: { present, expiresInSec },
 *     wrapperWillAccept: boolean,
 *     diagnosis: 'ok' | 'governance_missing' | 'governance_expired' |
 *                'governance_missing_workspace_claim' | 'sdk_only',
 *     suggestedAction: string
 *   }
 */
export const diagnoseAuth = () => {
  const { ref, parsed } = _readGovernanceTokenRaw()
  const sdkToken = _readSdkToken()

  const govAccessToken =
    parsed?.access_token ||
    parsed?.currentSession?.access_token ||
    null
  const govRefreshTokenPresent = !!(
    parsed?.refresh_token ||
    parsed?.currentSession?.refresh_token
  )
  const govClaims = _decodeJwt(govAccessToken)
  const govExp = _expiryStatus(govClaims?.exp)
  const govWorkspaceId = _claimWorkspaceId(govClaims)

  const sdkClaims = _decodeJwt(sdkToken)
  const sdkExp = _expiryStatus(sdkClaims?.exp)

  const governance = {
    ref,
    present: !!govAccessToken,
    accessToken: govAccessToken ? `${govAccessToken.slice(0, 12)}…` : null,
    expiresAt: govClaims?.exp ? new Date(govClaims.exp * 1000).toISOString() : null,
    expiresInSec: govExp.secondsLeft,
    expired: govExp.expired,
    userId: govClaims?.sub || null,
    email: govClaims?.email || govClaims?.app_metadata?.email || null,
    workspaceIdClaim: govWorkspaceId,
    missingClaim: !!govAccessToken && !govWorkspaceId,
    refreshTokenPresent: govRefreshTokenPresent,
  }
  const sdk = {
    present: !!sdkToken,
    expiresInSec: sdkExp.secondsLeft,
    expired: sdkExp.expired,
  }

  let diagnosis = 'ok'
  let suggestedAction = 'Auth state looks healthy — calls to /workspace/* should succeed.'

  if (!governance.present && !sdk.present) {
    diagnosis = 'all_missing'
    suggestedAction = 'No tokens in localStorage. Sign in via the federated login (loginAll).'
  } else if (!governance.present && sdk.present) {
    diagnosis = 'sdk_only'
    suggestedAction = 'SDK token present but governance Supabase JWT is missing. Federation never ran or its session was cleared. Sign out completely and sign in again — `loginAll()` will re-mint both.'
  } else if (governance.expired) {
    diagnosis = 'governance_expired'
    suggestedAction = governance.refreshTokenPresent
      ? 'Governance JWT is expired but a refresh token is present. The Supabase auth-js client should auto-refresh; if it isn\'t, force a re-login.'
      : 'Governance JWT is expired and no refresh token — sign in again.'
  } else if (governance.missingClaim) {
    diagnosis = 'governance_missing_workspace_claim'
    suggestedAction = 'Governance JWT is valid but lacks `workspace_id` (and `app_metadata.workspace_id` / `active_workspace_id`). Server-side: the symbols-auth-bridge edge function isn\'t writing the claim into app_metadata. Likely a backend config issue — verify the bridge edge function for the governance project.'
  }

  return {
    governance,
    sdk,
    wrapperWillAccept: governance.present && !governance.expired && !governance.missingClaim,
    diagnosis,
    suggestedAction,
  }
}

/**
 * Runs `diagnoseAuth()` and prints a human-readable summary to the
 * console — exposed as a global on window so it's invokable from the
 * browser console without an import path:
 *   > __auth.diagnose()
 *
 * Wired by bootShell at boot.
 */
export const runAuthDiagnostic = () => {
  const result = diagnoseAuth()
  // eslint-disable-next-line no-console
  console.group('[auth-diagnostic]')
  // eslint-disable-next-line no-console
  console.log('diagnosis:', result.diagnosis)
  // eslint-disable-next-line no-console
  console.log('suggestion:', result.suggestedAction)
  // eslint-disable-next-line no-console
  console.log('governance:', result.governance)
  // eslint-disable-next-line no-console
  console.log('sdk:', result.sdk)
  // eslint-disable-next-line no-console
  console.log('wrapper will accept calls:', result.wrapperWillAccept)
  // eslint-disable-next-line no-console
  console.groupEnd()
  return result
}
